import React from 'react'
import { differenceInCalendarDays } from 'date-fns'
import styled, { withTheme } from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import Button from 'components/lib/DesignSystem/Button'
import Icon from 'components/lib/Icon'

const SilentModeContent = styled.div`
  color: white;
  font-weight: 700;
  font-size: 12px;
  height: 32px;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: red;
`

const DemoModeContent = styled.div`
  width: 100%;
  height: 60px;
  padding: 11px 0;
  position: fixed;
  top: 0;
  left: 0;
  background: #ffffff;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  text-align: center;
  .icon-container {
    width: 37px;
    height: 37px;
    margin-right: 18px;
    border-radius: 37px;
    background: #e6e9ff;
    span {
      padding: 8.5px;
    }
  }
`

const StaticDemoContent = styled.div`
  width: 100%;
  height: 60px;
  padding: 11px 0;
  position: fixed;
  top: 0;
  left: 0;
  background: linear-gradient(0deg, rgba(252, 194, 33, 0.15), rgba(252, 194, 33, 0.15)), #ffffff;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  text-align: center;
  .icon-container {
    width: 37px;
    height: 37px;
    margin-right: 18px;
    border-radius: 37px;
    background: #fff;
    span {
      padding: 8.5px;
    }
  }
`

const ChildrenContainer = styled.div`
  height: ${(props) => `calc(100vh - ${props.fromTop}px)`};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: ${(props) => `translate(0, ${props.fromTop}px)`};
`

const DemoBannerContent = ({ mailboxesSyncing, handleSync, fromTop }) => (
  <DemoModeContent>
    <div className="dib-vac icon-container">
      <Icon imageName="space_astronaut_alternate" />
    </div>
    {mailboxesSyncing ? (
      <div className="dib-vac pc sb">Syncing mailboxes...</div>
    ) : (
      <>
        <div className="dib-vac pc tal">
          <div className="sb">You’re in Demo Mode viewing sample data</div>
          <div>Explore IRONSCALES until you’re ready to sync your mailboxes.</div>
        </div>
        <div className="dib-vac" style={{ marginLeft: '42px' }}>
          <Button
            id="demo-syncMailboxes-button"
            size="small"
            style={{ fontSize: '12px' }}
            text="Sync my mailboxes"
            onClick={handleSync}
          />
        </div>
      </>
    )}
    <ChildrenContainer fromTop={fromTop} />
  </DemoModeContent>
)

const StaticDemoBannerContent = ({ handleDisableDemoMode }) => (
  <StaticDemoContent>
    <div className="dib-vac icon-container">
      <Icon imageName="space_astronaut_alternate" fill="#162B62" />
    </div>
    <div className="dib-vac pc sb">You're in Demo Mode viewing sample data</div>
    <div className="dib-vac" style={{ marginLeft: '28px' }}>
      <Button
        id="static-demo-button"
        size="small"
        style={{
          fontSize: '12px',
          background: 'white',
          borderColor: '#C5C7D0',
          color: '#2F354A',
        }}
        text="Disable demo mode"
        onClick={handleDisableDemoMode}
      />
    </div>
  </StaticDemoContent>
)

const StarterFreeTrialBannerContent = ({ daysLeftToExpire, data }) => {
  const upgradeUrl = `https://www.ironscales.com/upgrade?tenant_id_cnc=${data.id}&tenant_domain_cnc=${data.domain}&partner_cnc=${data.isPartner}&enabled_mailboxes_cnc=${data.mailboxEnabled}&license_limit_cnc=${data.mailboxLimit}&email=${data.user.email}`
  return (
    <StaticDemoContent>
      <div className="dib-vac icon-container">
        <Icon imageName="space_astronaut_alternate" fill="#162B62" />
      </div>
      <div className="dib-vac pc sb">
        You’re enjoying a free upgrade to IRONSCALES Protect! {daysLeftToExpire + 1} days left to
        experience enhanced email security.{' '}
        <a
          id="Global_Banner_Link_To_Upgrade"
          href={upgradeUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          Learn more & upgrade here.
        </a>
      </div>
    </StaticDemoContent>
  )
}

const GlobalBanner = ({ data, showDemoBanner, mailboxesSyncing, theme, history, children }) => {
  const IS_SILENT_MODE = data.ff.SILENT_MODE_FF
  const IS_DEMO_MODE = showDemoBanner && window.innerWidth > parseInt(theme.rcPhonePoint)
  const IS_STATIC_DEMO_MODE =
    data.isStaticSalesDemo && window.innerWidth > parseInt(theme.rcPhonePoint)

  const isMspDashboard = matchURL(URL_CONST.partners.self)
  const isStarterFreeTrial = data.ff.FREE_IS_PROTECT
  const licenseExpiryDate = new Date(data.notifications.license_expiery)

  const handleSync = () => history.push(URL_CONST.mailboxes.upload)
  const handleDisableDemoMode = () => {
    history.push(URL_CONST.staticDemo.self)
    window.location.reload()
  }

  const renderConditions = {
    [isStarterFreeTrial]: () => (
      <>
        <StarterFreeTrialBannerContent
          daysLeftToExpire={differenceInCalendarDays(licenseExpiryDate, new Date())}
          data={data}
        />
        <ChildrenContainer fromTop={60}>{children}</ChildrenContainer>
      </>
    ),
    [IS_SILENT_MODE && !isMspDashboard]: () => (
      <>
        <SilentModeContent>Running in Silent Mode - No mitigation will occur</SilentModeContent>
        <ChildrenContainer fromTop={32}>{children}</ChildrenContainer>
      </>
    ),
    [IS_DEMO_MODE && !isMspDashboard]: () => (
      <>
        <DemoBannerContent
          mailboxesSyncing={mailboxesSyncing}
          handleSync={handleSync}
          fromTop={60}
        />
        <ChildrenContainer fromTop={60}>{children}</ChildrenContainer>
      </>
    ),
    [IS_STATIC_DEMO_MODE]: () => (
      <>
        <StaticDemoBannerContent handleDisableDemoMode={handleDisableDemoMode} />
        <ChildrenContainer fromTop={60}>{children}</ChildrenContainer>
      </>
    ),
    default: () => children,
  }

  const renderHandler = renderConditions.true || renderConditions.default

  return <> {renderHandler()} </>
}

export default withTheme(GlobalBanner)
